// Письма подбора репетиторов по заявке родителя (карта «Evə yaxın»).
//
// Чистые функции: их зовёт сервер (map-match.server.ts) и проверка
// scripts/verify-map-match.mts. Телефонов репетиторов в письме нет: кнопка
// WhatsApp ведёт на /api/map/go, сервер пишет клик и только там отдаёт
// номер опубликованной анкеты (как кнопка на карте).
//
//   parentMatchEmail — родителю: 1–5 карточек, «WhatsApp-a yaz», «Profilə bax»,
//                      «Hamısını xəritədə gör», «не подошло — ответьте», отписка.
//   parentManualEmail — родителю при no_match: «подбираем вручную».
//   followupEmail    — через 48 ч: «удалось найти?» да/нет.
//   tutorNoticeEmail — репетитору из подборки (по флагу): только факт, без контактов.
//   adminMatchEmail  — админу: заявка, кому ушло, баллы, пометки [TEST] и no_match.

import { GRADES, labelOf, subjectItemsFor } from "@/data/map-taxonomy";

type Lang = "az" | "ru" | "en";

export const SITE = "https://yolutap.com";
export const TUTORS_URL: Record<Lang, string> = { az: `${SITE}/az/tutors`, ru: `${SITE}/ru/tutors`, en: `${SITE}/en/tutors` };

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);

export const subjectName = (code: string, lang: Lang) => labelOf(subjectItemsFor("tutor"), code, lang);
export const gradeName = (code: string | null, lang: Lang) => (code ? labelOf(GRADES, code, lang) : "");

/** Карточка в письме — всё уже подписано на языке письма. */
export type ParentCard = {
  id: string;
  name: string;
  slug: string;
  subjects: string[];
  grades: string[];
  /** «Yasamal, m. Elmlər Akademiyası» или «Onlayn». */
  place: string;
  /** «15 ₼ / dərs · 60 dəq»; null — цены нет. */
  price: string | null;
  trial: boolean;
  /** Абсолютный адрес фото или null — тогда инициалы. */
  photo: string | null;
  initials: string;
  /** «Ведёт уроки на русском» — только у добранной на другом языке; иначе нет. */
  langNote?: string | null;
};

const LANG_IN: Record<Lang, Record<Lang, string>> = {
  az: { az: "Azərbaycan", ru: "rus", en: "ingilis" },
  ru: { az: "азербайджанском", ru: "русском", en: "английском" },
  en: { az: "Azerbaijani", ru: "Russian", en: "English" },
};

/**
 * Подпись анкеты, добранной на другом языке: на языке письма (или страницы) —
 * языки её уроков («Ведёт уроки на русском и английском»).
 */
export function teachesInLabel(lang: Lang, lessonLangs: Lang | readonly Lang[]): string {
  const list = (typeof lessonLangs === "string" ? [lessonLangs] : [...lessonLangs]).map((l) => LANG_IN[lang][l]);
  const and = { az: " və ", ru: " и ", en: " and " }[lang];
  const joined = list.length > 1 ? `${list.slice(0, -1).join(", ")}${and}${list.at(-1)}` : (list[0] ?? "");
  if (lang === "az") return `Dərsləri ${joined} ${list.length > 1 ? "dillərində" : "dilində"} keçir`;
  if (lang === "ru") return `Ведёт уроки на ${joined}`;
  return `Teaches in ${joined}`;
}

export type GoAction = "wa" | "profile" | "map";

/** Ссылка из письма: сервер пишет клик в map_request_events и уводит дальше. */
export const goUrl = (token: string, partnerId: string | null, a: GoAction) =>
  `${SITE}/api/map/go?t=${encodeURIComponent(token)}&a=${a}${partnerId ? `&p=${encodeURIComponent(partnerId)}` : ""}`;

/** Готовый текст первого сообщения репетитору в WhatsApp. */
export function waText(lang: Lang, subject: string, grade: string | null): string {
  const s = subjectName(subject, lang);
  const g = gradeName(grade, lang);
  if (lang === "ru") return `Здравствуйте! Пишу с Yolutap, ищу репетитора по предмету «${s}»${g ? ` (${g})` : ""}. Можно узнать подробности?`;
  if (lang === "en") return `Hello! I found you on Yolutap. I'm looking for a ${s} tutor${g ? ` (${g})` : ""}. Could you tell me more?`;
  return `Salam! Yolutap-dan yazıram, ${s} üzrə ${g ? `${g} üçün ` : ""}repetitor axtarıram. Ətraflı məlumat ala bilərəm?`;
}

function ruPlural(n: number, one: string, few: string, many: string): string {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return many;
  if (b === 1) return one;
  if (b >= 2 && b <= 4) return few;
  return many;
}

const T = {
  az: {
    subject: (n: number, s: string, place: string) => `Sizin üçün ${n} repetitor: ${s}, ${place}`,
    hello: "Salam!",
    intro: (s: string) => `Müraciətinizə əsasən ${s} üzrə uyğun repetitorları seçdik. Bəyəndiyinizə birbaşa WhatsApp-da yazın.`,
    wa: "WhatsApp-a yaz",
    profile: "Profilə bax",
    trial: "Pulsuz sınaq dərsi",
    all: "Hamısını xəritədə gör",
    noFit: "Heç biri uyğun gəlmədi? Bu məktuba cavab verin — əl ilə seçəcəyik.",
    unsub: "Bu məktubları almaq istəmirsinizsə, abunəlikdən çıxın",
    team: "Yolutap komandası",
    online: "Onlayn",
  },
  ru: {
    subject: (n: number, s: string, place: string) => `${n} ${ruPlural(n, "репетитор", "репетитора", "репетиторов")} для вас: ${s}, ${place}`,
    hello: "Здравствуйте!",
    intro: (s: string) => `По вашей заявке мы подобрали репетиторов по предмету «${s}». Понравившемуся можно сразу написать в WhatsApp.`,
    wa: "Написать в WhatsApp",
    profile: "Профиль",
    trial: "Пробный урок бесплатно",
    all: "Посмотреть всех на карте",
    noFit: "Не подошло? Ответьте на это письмо — подберём вручную.",
    unsub: "Не хотите получать такие письма — отписаться",
    team: "Команда Yolutap",
    online: "Онлайн",
  },
  en: {
    subject: (n: number, s: string, place: string) => `${n} ${n === 1 ? "tutor" : "tutors"} for you: ${s}, ${place}`,
    hello: "Hello!",
    intro: (s: string) => `Based on your request, we've picked ${s} tutors who fit. Message the one you like on WhatsApp directly.`,
    wa: "Message on WhatsApp",
    profile: "View profile",
    trial: "Free trial lesson",
    all: "See everyone on the map",
    noFit: "None of them fit? Reply to this email and we'll pick by hand.",
    unsub: "Don't want these emails? Unsubscribe",
    team: "The Yolutap team",
    online: "Online",
  },
} as const;

const btn = (href: string, label: string, primary: boolean) =>
  `<a href="${esc(href)}" style="display:inline-block;margin:8px 8px 0 0;padding:9px 14px;border-radius:8px;text-decoration:none;font-weight:700;font-size:14px;${primary ? "background:#25d366;color:#ffffff" : "border:1px solid #1f2937;color:#1f2937"}">${esc(label)}</a>`;

function cardHtml(c: ParentCard, lang: Lang, token: string): string {
  const t = T[lang];
  const avatar = c.photo
    ? `<img src="${esc(c.photo)}" width="56" height="56" alt="" style="width:56px;height:56px;border-radius:50%;object-fit:cover;display:block">`
    : `<div style="width:56px;height:56px;border-radius:50%;background:#fde68a;color:#1f2937;font-weight:700;font-size:20px;line-height:56px;text-align:center">${esc(c.initials)}</div>`;
  const lines = [
    c.subjects.join(" · "),
    c.grades.join(", "),
    c.place,
    c.price,
  ].filter((x): x is string => !!x);
  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:0 0 14px;border:1px solid #e5e7eb;border-radius:12px"><tr>
<td width="72" valign="top" style="padding:14px 0 14px 14px">${avatar}</td>
<td valign="top" style="padding:14px">
<div style="font-size:16px;font-weight:700;color:#111827">${esc(c.name)}</div>
${lines.map((l) => `<div style="font-size:14px;color:#4b5563;margin-top:2px">${esc(l)}</div>`).join("")}
${c.langNote ? `<div style="font-size:13px;color:#b45309;font-weight:700;margin-top:4px">${esc(c.langNote)}</div>` : ""}
${c.trial ? `<div style="font-size:13px;color:#047857;font-weight:700;margin-top:4px">${esc(t.trial)}</div>` : ""}
<div>${btn(goUrl(token, c.id, "wa"), t.wa, true)}${btn(goUrl(token, c.id, "profile"), t.profile, false)}</div>
</td></tr></table>`;
}

function cardText(c: ParentCard, lang: Lang, token: string): string {
  const t = T[lang];
  return [
    `• ${c.name}`,
    ...[c.subjects.join(" · "), c.grades.join(", "), c.place, c.price, c.langNote ?? null, c.trial ? t.trial : null].filter((x): x is string => !!x).map((l) => `  ${l}`),
    `  ${t.wa}: ${goUrl(token, c.id, "wa")}`,
    `  ${t.profile}: ${goUrl(token, c.id, "profile")}`,
  ].join("\n");
}

const shell = (body: string) =>
  `<div style="font-family:Arial,Helvetica,sans-serif;font-size:15px;line-height:1.5;color:#111827;max-width:560px">${body}</div>`;

export function parentMatchEmail(a: {
  lang: Lang;
  requestId: string;
  token: string;
  subject: string;
  grade: string | null;
  /** Район, город или «онлайн» — на языке письма. */
  place: string;
  cards: ParentCard[];
  unsubscribeUrl: string;
}) {
  const t = T[a.lang];
  const s = subjectName(a.subject, a.lang);
  const html = shell(
    `<p>${esc(t.hello)}</p><p>${esc(t.intro(s))}</p>` +
      a.cards.map((c) => cardHtml(c, a.lang, a.token)).join("") +
      `<p><a href="${esc(goUrl(a.token, null, "map"))}" style="color:#1f2937;font-weight:700">${esc(t.all)} →</a></p>` +
      `<p>${esc(t.noFit)}</p><p>${esc(t.team)}</p>` +
      `<p style="font-size:12px;color:#6b7280"><a href="${esc(a.unsubscribeUrl)}" style="color:#6b7280">${esc(t.unsub)}</a></p>`,
  );
  const text = [t.hello, t.intro(s), ...a.cards.map((c) => cardText(c, a.lang, a.token)), `${t.all}: ${goUrl(a.token, null, "map")}`, t.noFit, t.team, `${t.unsub}: ${a.unsubscribeUrl}`].join("\n\n");
  return { subject: t.subject(a.cards.length, s, a.place), html, text };
}

const MANUAL = {
  az: { subject: "Müraciətiniz alındı — repetitoru əl ilə seçirik", body: ["Salam!", "Müraciətinizə uyğun repetitoru hələ avtomatik tapa bilmədik. Komandamız əl ilə seçəcək və bir gün ərzində sizinlə əlaqə saxlayacaq.", "Yolutap komandası"] },
  ru: { subject: "Заявка получена — подбираем репетитора вручную", body: ["Здравствуйте!", "Автоматически подходящего репетитора пока не нашлось. Мы подберём вручную и свяжемся с вами в течение дня.", "Команда Yolutap"] },
  en: { subject: "Request received — we're picking a tutor by hand", body: ["Hello!", "We couldn't find a matching tutor automatically yet. Our team will pick one by hand and get in touch within a day.", "The Yolutap team"] },
} as const;

export function parentManualEmail(lang: Lang, unsubscribeUrl: string) {
  const m = MANUAL[lang];
  const unsub = T[lang].unsub;
  return {
    subject: m.subject,
    html: shell(m.body.map((p) => `<p>${esc(p)}</p>`).join("") + `<p style="font-size:12px;color:#6b7280"><a href="${esc(unsubscribeUrl)}" style="color:#6b7280">${esc(unsub)}</a></p>`),
    text: [...m.body, `${unsub}: ${unsubscribeUrl}`].join("\n\n"),
  };
}

const FOLLOWUP = {
  az: { subject: (s: string) => `Repetitor tapa bildinizmi? (${s})`, q: (s: string) => `İki gün əvvəl sizə ${s} üzrə repetitor seçimi göndərmişdik. Uyğun repetitor tapa bildinizmi?`, yes: "Bəli, tapdım", no: "Xeyr, kömək lazımdır", hello: "Salam!" },
  ru: { subject: (s: string) => `Удалось найти репетитора? (${s})`, q: (s: string) => `Два дня назад мы прислали подборку репетиторов по предмету «${s}». Удалось найти подходящего?`, yes: "Да, нашли", no: "Нет, нужна помощь", hello: "Здравствуйте!" },
  en: { subject: (s: string) => `Did you find a tutor? (${s})`, q: (s: string) => `Two days ago we sent you a list of ${s} tutors. Did you find one who fits?`, yes: "Yes, we did", no: "No, we need help", hello: "Hello!" },
} as const;

export const feedbackUrl = (token: string, answer: "yes" | "no") => `${SITE}/api/map/feedback?t=${encodeURIComponent(token)}&a=${answer}`;

export function followupEmail(a: { lang: Lang; token: string; subject: string; unsubscribeUrl: string }) {
  const f = FOLLOWUP[a.lang];
  const t = T[a.lang];
  const s = subjectName(a.subject, a.lang);
  return {
    subject: f.subject(s),
    html: shell(
      `<p>${esc(f.hello)}</p><p>${esc(f.q(s))}</p><div>${btn(feedbackUrl(a.token, "yes"), f.yes, true)}${btn(feedbackUrl(a.token, "no"), f.no, false)}</div><p>${esc(t.team)}</p>` +
        `<p style="font-size:12px;color:#6b7280"><a href="${esc(a.unsubscribeUrl)}" style="color:#6b7280">${esc(t.unsub)}</a></p>`,
    ),
    text: [f.hello, f.q(s), `${f.yes}: ${feedbackUrl(a.token, "yes")}`, `${f.no}: ${feedbackUrl(a.token, "no")}`, t.team, `${t.unsub}: ${a.unsubscribeUrl}`].join("\n\n"),
  };
}

const NOTICE = {
  az: { subject: (s: string) => `Sizə valideyn sorğusu gəldi: ${s}`, body: (line: string) => [`Sizə valideyn sorğusu gəldi: ${line}.`, "Valideyn sizinlə WhatsApp-da əlaqə saxlaya bilər. Telefonunuzun yanında olun.", "Yolutap komandası"] },
  ru: { subject: (s: string) => `Вам пришёл запрос родителя: ${s}`, body: (line: string) => [`Вам пришёл запрос родителя: ${line}.`, "Родитель может написать вам в WhatsApp. Держите телефон под рукой.", "Команда Yolutap"] },
  en: { subject: (s: string) => `A parent request matched you: ${s}`, body: (line: string) => [`A parent request matched your profile: ${line}.`, "The parent may message you on WhatsApp.", "The Yolutap team"] },
} as const;

/** Репетитору — только факт заявки: предмет, класс, район. Без телефона и почты родителя. */
export function tutorNoticeEmail(a: { lang: Lang; subject: string; grade: string | null; place: string }) {
  const n = NOTICE[a.lang];
  const s = subjectName(a.subject, a.lang);
  const line = [s, gradeName(a.grade, a.lang), a.place].filter(Boolean).join(", ");
  const body = n.body(line);
  return { subject: n.subject(s), html: shell(body.map((p) => `<p>${esc(p)}</p>`).join("")), text: body.join("\n\n") };
}

/** Строка подборки для админа. otherLangs — добрана на другом языке (её языки уроков). */
export type AdminPick = { name: string; slug: string; score: number; parts: Record<string, number>; otherLangs?: Lang[] };

/** Почему подборка не ушла родителю — ровно одна причина. code — у ошибки отправки. */
export type ParentSkip = { reason: "no_email" | "no_consent" | "suppressed" | "test" | "send_error"; code?: string };

/** Строка уведомления «не ушла» — одна конкретная причина. */
export function parentSkipNote(s: ParentSkip): string {
  const why = {
    no_email: "почты нет",
    no_consent: "нет согласия на письмо с подборкой",
    suppressed: "адрес отписан от писем",
    test: "тестовая заявка",
    send_error: `ошибка отправки (код: ${s.code || "неизвестен"})`,
  }[s.reason];
  return `Подборка родителю не ушла: ${why}.`;
}

export const RELAXED_LABEL: Record<string, string> = { place: "место (соседние районы и онлайн)", budget: "бюджет", lang: "язык (добраны репетиторы на другом языке)" };

export function adminMatchEmail(a: {
  isTest: boolean;
  testReason: string | null;
  status: "new" | "sent" | "matched" | "no_match" | "closed" | "spam";
  duplicateOf: string | null;
  relaxed: string[];
  lines: [string, string][];
  picks: AdminPick[];
  parentEmailed: boolean;
  /** Почему не ушло — если не ушло. */
  parentSkip?: ParentSkip | null;
  /** Ссылка wa.me на номер родителя с подборкой — если почты нет. */
  parentWaLink: string | null;
  adminUrl: string;
  subjectLine: string;
}) {
  const tags = [a.isTest ? "[TEST]" : "", a.status === "no_match" ? "[НЕТ ПОДБОРА]" : "", a.duplicateOf ? "[ПОВТОР]" : ""].filter(Boolean).join(" ");
  const subject = `${tags ? `${tags} ` : ""}Заявка с карты: ${a.subjectLine}`;
  const pickRows = a.picks.map((p) => [p.otherLangs?.length ? `${p.name} [ведёт уроки на ${p.otherLangs.map((l) => l.toUpperCase()).join(", ")}]` : p.name, `${p.score} (место ${p.parts.place}, цена ${p.parts.price}, язык ${p.parts.lang}, анкета ${p.parts.profile}, пробный ${p.parts.trial}, свежесть ${p.parts.fresh}, ротация ${p.parts.rotation})`, `${TUTORS_URL.ru}/${p.slug}`]);
  const notes = [
    a.isTest ? `Тестовая заявка (${a.testReason}). Родителю письмо не отправлялось.` : "",
    a.duplicateOf ? `Повтор заявки ${a.duplicateOf} за 24 часа — подборка повторно не отправлялась.` : "",
    a.status === "no_match" ? "Подобрать не удалось. Родителю ушло «подбираем вручную, свяжемся в течение дня». Нужна ручная помощь." : "",
    a.relaxed.length ? `Условия ослаблены: ${a.relaxed.map((r) => RELAXED_LABEL[r] ?? r).join(", ")}.` : "",
    a.status === "new" && !a.duplicateOf ? "Без автоподбора: кружок, предмет не выбран или подбор не выполнился." : "",
    !a.isTest && !a.duplicateOf && a.status !== "no_match" && a.status !== "new" ? (a.parentEmailed ? "Подборка отправлена родителю на почту." : parentSkipNote(a.parentSkip ?? { reason: "send_error" })) : "",
    a.parentWaLink ? `Отправить родителю в WhatsApp: ${a.parentWaLink}` : "",
  ].filter(Boolean);
  const html =
    `<div style="font-family:Arial,sans-serif;font-size:14px;line-height:1.5">` +
    notes.map((n) => `<p><b>${esc(n)}</b></p>`).join("") +
    `<table cellpadding="6">${a.lines.map(([k, v]) => `<tr><td><b>${esc(k)}</b></td><td>${esc(v)}</td></tr>`).join("")}</table>` +
    (pickRows.length ? `<p><b>Подборка</b></p><ol>${pickRows.map(([n, s, u]) => `<li><a href="${esc(u!)}">${esc(n!)}</a> — ${esc(s!)}</li>`).join("")}</ol>` : "") +
    `<p><a href="${esc(a.adminUrl)}">Открыть в /admin</a></p></div>`;
  const text = [...notes, ...a.lines.map(([k, v]) => `${k}: ${v}`), ...(pickRows.length ? ["Подборка:", ...pickRows.map(([n, s, u], i) => `${i + 1}. ${n} — ${s} — ${u}`)] : []), `/admin: ${a.adminUrl}`].join("\n");
  return { subject, html, text };
}
